import { shopperAgentOutreachConditionLabel } from "@redotech/redo-flows-common/rules-v3/types";
import {
  RuleActionType,
  RuleConditionType,
} from "@redotech/redo-flows-common/rules/types";
import { t } from "@redotech/redo-i18n";
import { ConditionSummarySegment } from "../condition-summary";

interface RuleConditionLike {
  conditionType?: RuleConditionType;
  [key: string]: unknown;
}

interface ConditionGroupLike {
  operator?: string;
  conditions?: (ConditionGroupLike | RuleConditionLike)[];
}

interface RuleActionLike {
  actionType?: RuleActionType;
  [key: string]: unknown;
}

function conditionLabels(): Record<RuleConditionType, string> {
  return {
    [RuleConditionType.USER_IS_AVAILABLE]: t("User is available"),
    [RuleConditionType.TICKET_CHANNEL]: t("Ticket channel"),
    [RuleConditionType.TICKET_STATUS]: t("Ticket status"),
    [RuleConditionType.TOPIC]: t("Topic"),
    [RuleConditionType.SENT_FROM_EMAIL]: t("Sent from email"),
    [RuleConditionType.RECEIVED_AT_EMAIL]: t("Received at email"),
    [RuleConditionType.SUBJECT_LINE]: t("Subject line"),
    [RuleConditionType.MESSAGE_BODY_MATCHES]: t("Message body"),
    [RuleConditionType.AFTER_HOURS]: t("After hours"),
    [RuleConditionType.TIME_BASED]: t("Time"),
    [RuleConditionType.ASSIGNMENT_TYPE]: t("Assignment type"),
    [RuleConditionType.LAST_ORDER_TIME]: t("Customer last order"),
    [RuleConditionType.LAST_ORDER]: t("Customer last order"),
    [RuleConditionType.MESSAGE_SENTIMENT]: t("Message sentiment"),
    [RuleConditionType.HAS_TAG]: t("Ticket tags"),
    [RuleConditionType.TRIGGER_TAG]: t("Trigger tag"),
    [RuleConditionType.RESPONSE_TIME]: t("Time since last response"),
    [RuleConditionType.SENT_FROM]: t("Sent from"),
    [RuleConditionType.RECEIVED_AT]: t("Received at"),
    [RuleConditionType.AI_STATUS]: t("AI status"),
    [RuleConditionType.ASSIGNMENT]: t("Assignment"),
    [RuleConditionType.MESSAGE_COUNT]: t("Message count"),
    [RuleConditionType.MESSAGE_TYPE]: t("Message type"),
    [RuleConditionType.RANDOM_PERCENTAGE]: t("Random percentage"),
    [RuleConditionType.PREVIOUS_TICKET_STATUS]: t("Previous ticket status"),
    [RuleConditionType.SHOPPER_AGENT_CONVERSATION]: t(
      "Shopper AI conversation",
    ),
    [RuleConditionType.SHOPPER_AGENT_OUTREACH]: t("{{productName}} outreach", {
      productName: "Shopper Agent",
    }),
    [RuleConditionType.MESSAGE_STORY_KIND]: t(
      "{{integrationName}} story kind",
      { integrationName: "Instagram" },
    ),
  };
}

function actionLabels(): Record<RuleActionType, string> {
  return {
    [RuleActionType.SET_USER_AVAILABILITY]: t("Set user availability"),
    [RuleActionType.SET_STATUS]: t("Set status"),
    [RuleActionType.SET_STATUS_V3]: t("Set status"),
    [RuleActionType.ADD_TAG]: t("Add tag"),
    [RuleActionType.REMOVE_TAG]: t("Remove tag"),
    [RuleActionType.AUTO_REPLY]: t("Auto reply"),
    [RuleActionType.ASSIGN]: t("Assign"),
    [RuleActionType.ASSIGN_V3]: t("Assign"),
    [RuleActionType.ASSIGN_OLDEST_TICKET]: t("Assign oldest ticket"),
    [RuleActionType.LIKE_COMMENT]: t("Like comment"),
    [RuleActionType.HIDE_COMMENT]: t("Hide comment"),
    [RuleActionType.SET_PRIORITY]: t("Set priority"),
    [RuleActionType.START_AI_AGENT_EXECUTION]: t("AI response"),
    [RuleActionType.HANDOFF_AI]: t("Handoff AI"),
    [RuleActionType.GENERATE_CONVERSATION_INSIGHTS]: t("Generate insights"),
    [RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE]: t(
      "Generate merchant knowledge",
    ),
    [RuleActionType.SEND_CSAT_SURVEY]: t("Send CSAT survey"),
    [RuleActionType.MERGE_SUPPORT_TICKET]: t("Merge support ticket"),
    [RuleActionType.SPLIT_SUPPORT_TICKET]: t("Split support ticket"),
    [RuleActionType.STOP_TOPIC_CONTINUATION]: t("Stop ticket continuation"),
    [RuleActionType.RUN_CUSTOM_CODE]: t("Run custom code"),
  };
}

function isConditionGroup(
  condition: ConditionGroupLike | RuleConditionLike,
): condition is ConditionGroupLike {
  return "conditions" in condition;
}

/**
 * Keys carrying structure or plumbing rather than merchant-visible config.
 * Raw ids are dropped separately: nothing here can resolve them to names.
 */
const IGNORED_KEYS = new Set([
  "conditionType",
  "actionType",
  "queryOperator",
  "channel",
  "property",
  "timezone",
  "htmlContent",
  "attachments",
  "headerImage",
  "signatureSeeded",
]);

const isIdKey = (key: string) => /Ids?$/.test(key);

function formatConfigValue(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    return [trimmed];
  }
  if (typeof value === "number") {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(formatConfigValue);
  }
  if (value && typeof value === "object") {
    return configValues(value as Record<string, unknown>);
  }
  return [];
}

/**
 * Every configured value on an object. `_tag` names a union variant, so it is
 * a fallback used only when that variant has nothing else to show.
 */
function configValues(source: Record<string, unknown>): string[] {
  const values = Object.entries(source).flatMap(([key, value]) =>
    IGNORED_KEYS.has(key) || isIdKey(key) || key === "_tag"
      ? []
      : formatConfigValue(value),
  );
  if (values.length) {
    return values;
  }
  return typeof source._tag === "string" ? [source._tag] : [];
}

/** True when a condition declares configuration, even if currently empty. */
function hasConfigurableFields(condition: RuleConditionLike): boolean {
  return Object.keys(condition).some(
    (key) => !IGNORED_KEYS.has(key) && !isIdKey(key),
  );
}

function getConfiguredValue(source: RuleConditionLike): string | undefined {
  if (typeof source.percentage === "number") {
    return `${source.percentage}%`;
  }
  if (
    source.conditionType === RuleConditionType.SHOPPER_AGENT_OUTREACH &&
    Array.isArray(source.outreachTypes)
  ) {
    const labels = source.outreachTypes.flatMap((value) => {
      const label =
        typeof value === "string"
          ? shopperAgentOutreachConditionLabel(value)
          : undefined;
      return label ? [label] : [];
    });
    return labels.length ? labels.join(", ") : undefined;
  }
  const values = configValues(source);
  return values.length ? values.join(", ") : undefined;
}

const text = (value: string): ConditionSummarySegment => ({
  kind: "text",
  text: value,
});
const variable = (value: string): ConditionSummarySegment => ({
  kind: "variable",
  text: value,
});
const configured = (value: string): ConditionSummarySegment => ({
  kind: "value",
  text: value,
});

/** One rendered line, so the and/or joiners stack into a scannable column. */
export type SummaryLine = ConditionSummarySegment[];

/**
 * Reads as the inspector panel states it: label, operator, value. Dropping the
 * operator would turn "Ticket status is not Open" into its exact opposite.
 */
function summarizeCondition(condition: RuleConditionLike): SummaryLine {
  if (!condition.conditionType) {
    return [];
  }
  const label = variable(conditionLabels()[condition.conditionType]);
  const value = getConfiguredValue(condition);
  if (!value) {
    return hasConfigurableFields(condition)
      ? [label, text(t("not set"))]
      : [label];
  }
  const operator =
    typeof condition.queryOperator === "string"
      ? condition.queryOperator.replaceAll("_", " ")
      : t("is");
  return [label, text(operator), configured(value)];
}

/**
 * One line per condition, led by this group's own AND/OR so a nested group
 * keeps its real operator. Nesting is parenthesized: "a and b or c" is unclear.
 */
function summarizeGroup(group: ConditionGroupLike): SummaryLine[] {
  const joiner = group.operator === "OR" ? t("or") : t("and");
  const lines = (group.conditions ?? []).flatMap((condition): SummaryLine[] => {
    if (!isConditionGroup(condition)) {
      const summary = summarizeCondition(condition);
      return summary.length ? [summary] : [];
    }
    const nested = summarizeGroup(condition);
    if (!nested.length) {
      return [];
    }
    return [[text("("), ...nested.flat(), text(")")]];
  });
  return lines.map((line, index) =>
    index === 0 ? line : [text(joiner), ...line],
  );
}

export function getRuleConditionsSummary(
  group: ConditionGroupLike | undefined,
): SummaryLine[] {
  const [first, ...rest] = group ? summarizeGroup(group) : [];
  return first
    ? [[text(t("If")), ...first], ...rest]
    : [[text(t("No conditions"))]];
}

/**
 * One line per action rather than a count, so the block does not have to be
 * opened to learn what else the rule does.
 */
export function getRuleActionsSummary(
  actions: RuleActionLike[] | undefined,
): SummaryLine[] {
  const lines = (actions ?? []).flatMap((action): SummaryLine[] => {
    if (!action.actionType) {
      return [];
    }
    const label = variable(actionLabels()[action.actionType]);
    const value =
      typeof action.status === "string"
        ? action.status.toLowerCase().replaceAll("_", " ")
        : getConfiguredValue(action);
    return [value ? [label, configured(value)] : [label]];
  });
  return lines.length ? lines : [[text(t("No actions"))]];
}
